import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { congesApi } from '../../services/conges-api';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Champ,
  Saisie,
  Section,
  Selection,
  ZoneTexte,
} from '../ndf/ui/Champ';
import { MessageErreur } from '../ndf/ui/MessageEtat';
import { erreurGlobale } from '../ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';
import { useNdfMoi } from '../../hooks/useNdfMoi';
import { dateFr } from './arrets-commun';
// `annule_le` est un HORODATAGE, pas une date pure : le lire en UTC affichait
// l'annulation la veille entre minuit et deux heures du matin, heure de Paris.
import { jourAParis } from '../../../server/lib/dates';
import { useTypesArret } from '../../hooks/useTypesArret';

/**
 * Le détail d'un arrêt de travail, et ce qu'on peut en faire (R-AT-06).
 *
 * Le même panneau sert la fiche salarié et l'onglet de pilotage : deux détails
 * différents auraient fini par proposer des actions différentes selon l'endroit
 * d'où on ouvre le même arrêt.
 *
 * Les actions propres au contexte (prolonger, ajouter une attestation) sont
 * fournies par l'écran appelant, qui possède déjà ces formulaires.
 */
export function DetailArret({
  arretId,
  onFerme,
  actions,
}: {
  arretId: string;
  onFerme: () => void;
  actions?: ReactNode;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const moi = useNdfMoi();
  const { types: typesArret } = useTypesArret();
  const [edition, setEdition] = useState(false);
  const [annulation, setAnnulation] = useState(false);
  const [motif, setMotif] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'arret', arretId],
    queryFn: () => congesApi.absencesRh.arret(arretId),
  });

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['conges', 'arret', arretId] });
    qc.invalidateQueries({ queryKey: ['conges', 'arrets'] });
    qc.invalidateQueries({ queryKey: ['conges', 'absences-rh'] });
  };

  const [type, setType] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [commentaire, setCommentaire] = useState('');

  const ouvrirEdition = () => {
    if (!data) return;
    setType(data.arret.type_arret);
    setDebut(data.arret.date_debut);
    setFin(data.arret.date_fin);
    setCommentaire(data.arret.commentaire ?? '');
    setEdition(true);
  };

  const modifier = useMutation({
    mutationFn: (confirmeDuree: boolean) =>
      congesApi.absencesRh.modifierArret(arretId, {
        type_arret: type,
        date_debut: debut,
        date_fin: fin,
        commentaire: commentaire.trim() ? commentaire.trim() : null,
        confirme_duree: confirmeDuree,
      }),
    onSuccess: () => {
      toast.success('Arrêt corrigé.');
      setEdition(false);
      rafraichir();
    },
    onError: (e: unknown) => {
      // R-AT-06 : au-delà d'un an, on confirme une fois plutôt que de refuser.
      const corps = (e as { body?: { confirmation_requise?: string; error?: string } })?.body;
      if (corps?.confirmation_requise === 'duree') {
        if (window.confirm(`${corps.error} Confirmer ces dates ?`)) modifier.mutate(true);
        return;
      }
      toast.error(erreurGlobale(e) ?? 'La correction a échoué.');
    },
  });

  const annuler = useMutation({
    mutationFn: () => congesApi.absencesRh.annulerArret(arretId, motif),
    onSuccess: () => {
      toast.success('Arrêt annulé. L’absence liée a été annulée et les compteurs recrédités.');
      setAnnulation(false);
      setMotif('');
      rafraichir();
      onFerme();
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'L’annulation a échoué.'),
  });

  if (isLoading) return <p className="p-4 text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!data) return null;

  const a = data.arret;
  const estAnnule = a.statut === 'annule';

  return (
    <Section
      titre={`Arrêt du ${dateFr(a.date_debut)}`}
      teinte={estAnnule ? 'ciel' : 'corail'}
      actions={
        <BoutonSecondaire type="button" onClick={onFerme}>
          Fermer
        </BoutonSecondaire>
      }
    >
      {estAnnule && (
        <p className="mb-3 rounded-lg border border-nuit/20 bg-nuit/5 p-3 text-sm text-nuit/80">
          Arrêt annulé le {a.annule_le ? dateFr(jourAParis(new Date(a.annule_le))) : ''} par{' '}
          {a.annule_par ?? ''}.
          {a.motif_annulation ? ` Motif : ${a.motif_annulation}` : ''}
        </p>
      )}

      {!edition ? (
        <>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-nuit/60">Type d’arrêt</dt>
              <dd className="text-nuit">{a.type_arret}</dd>
            </div>
            <div>
              <dt className="text-nuit/60">Période</dt>
              <dd className="text-nuit">
                {dateFr(a.date_debut)} au {dateFr(a.date_fin)}
              </dd>
            </div>
            {a.commentaire && (
              <div className="sm:col-span-2">
                <dt className="text-nuit/60">Commentaire</dt>
                <dd className="text-nuit">{a.commentaire}</dd>
              </div>
            )}
          </dl>

          {data.prolongations.length > 0 && (
            <div className="mt-3 text-sm">
              <p className="text-nuit/60">Prolongations</p>
              <ul className="mt-1 space-y-1">
                {data.prolongations.map((p) => (
                  <li key={p.id} className="text-nuit">
                    {dateFr(p.date_debut)} au {dateFr(p.date_fin)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.pieces.length > 0 && (
            <div className="mt-3 text-sm">
              <p className="text-nuit/60">Documents</p>
              <ul className="mt-1 space-y-1">
                {data.pieces.map((p) => (
                  <li key={p.id} className="text-nuit">
                    {p.filename}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!estAnnule && (
            <div className="mt-4 flex flex-wrap gap-2">
              {actions}
              <BoutonSecondaire type="button" onClick={ouvrirEdition}>
                Modifier
              </BoutonSecondaire>
              {moi.congesAdministrer && (
                <BoutonSecondaire type="button" onClick={() => setAnnulation(true)}>
                  Annuler l’arrêt
                </BoutonSecondaire>
              )}
            </div>
          )}
        </>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            modifier.mutate(false);
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Champ label="Type d’arrêt">
              <Selection
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={data.prolongations.length > 0}
              >
                {typesArret.map((t) => (
                  <option key={t.id} value={t.libelle}>
                    {t.libelle}
                  </option>
                ))}
              </Selection>
            </Champ>
            <div className="grid grid-cols-2 gap-3">
              <Champ label="Du">
                <Saisie type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
              </Champ>
              <Champ label="Au">
                <Saisie type="date" min={debut} value={fin} onChange={(e) => setFin(e.target.value)} />
              </Champ>
            </div>
          </div>
          {data.prolongations.length > 0 && (
            <p className="text-xs text-nuit/60">
              Cet arrêt porte des prolongations : son type ne peut plus changer. Pour en changer,
              annulez l’épisode et ressaisissez-le.
            </p>
          )}
          <Champ label="Commentaire">
            <ZoneTexte value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </Champ>
          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal type="submit" disabled={modifier.isPending}>
              Enregistrer
            </BoutonPrincipal>
            <BoutonSecondaire type="button" onClick={() => setEdition(false)}>
              Annuler
            </BoutonSecondaire>
          </div>
        </form>
      )}

      {annulation && (
        <form
          className="mt-4 space-y-3 border-t border-nuit/10 pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            annuler.mutate();
          }}
        >
          <p className="text-sm text-nuit/70">
            L’arrêt restera lisible, marqué annulé. L’absence liée sera annulée et les compteurs
            recrédités. Le motif est obligatoire.
          </p>
          <Champ label="Motif de l’annulation">
            <ZoneTexte value={motif} onChange={(e) => setMotif(e.target.value)} />
          </Champ>
          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal type="submit" disabled={annuler.isPending || motif.trim().length < 3}>
              Confirmer l’annulation
            </BoutonPrincipal>
            <BoutonSecondaire type="button" onClick={() => setAnnulation(false)}>
              Revenir
            </BoutonSecondaire>
          </div>
        </form>
      )}
    </Section>
  );
}
